import React, { useState, useEffect, useMemo, Fragment } from "react";
import { timeConversion } from "client-app-core";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import debounce from "debounce";

import { Typography, Button, Drawer, Box, Tooltip } from "@mui/material";

import ErrorBoundary from "orion-components/ErrorBoundary";
import { TargetingIcon } from "orion-components/SharedComponents";
import SpriteIcon from "orion-components/SharedComponents/SpriteIcon/SpriteIcon";
import { DataGrid } from "orion-components/CBComponents";
import { getEntityIconsByType } from "orion-components/GlobalData/Selectors";
import { getDir } from "orion-components/i18n/Config/selector";
import { acknowledgeAlerts, unAcknowledgeAlerts } from "orion-components/Dock/Alerts/actions";
import { acknowledgeNotifications, unAcknowledgeNotifications } from "orion-components/Dock/Notifications/actions";
import EventBus from "orion-components/SharedComponents/EventBus";
import { Translate, getTranslation } from "orion-components/i18n";
import { AlertPanelDetailWidget } from "orion-components/Profiles/Widgets";
import { openAlertProfile } from "orion-components/Profiles/AlertProfile/actions";
import { setLocalAppState } from "orion-components/AppState/Actions";
import { getEcoSettingByKey } from "orion-components/Services/EcoSettingService/selectors";
import SettingsMenu from "orion-components/Dock/Notifications/components/SettingsMenu";
import FilterSettings from "orion-components/Dock/Notifications/components/FilterSettings";
import AlertTimerCell from "./components/AlertTimerCell";
import { processAggregates } from "../trans-utils";

const propTypes = {
	notifications: PropTypes.array,
	forReplay: PropTypes.bool,
	readOnly: PropTypes.bool,
	selectFloorPlanOn: PropTypes.func,
	floorPlansWithFacilityFeed: PropTypes.object,
	disableNotificationPanel: PropTypes.bool
};

const defaultProps = {
	notifications: [],
	disableNotificationPanel: false
};

const BottomAlertList = ({
	notifications,
	forReplay,
	readOnly,
	selectFloorPlanOn,
	floorPlansWithFacilityFeed,
	isBottomDock,
	disableNotificationPanel,
	actionIcons,
	disableTargetingIcons
}) => {
	const dispatch = useDispatch();

	const timeFormatPreference = useSelector(state => state.appState.global.timeFormat);
	const locale = useSelector(state => state.i18n.locale);
	const colors = useSelector(state => getEntityIconsByType(state)("frame_colors"));
	const dir = useSelector(state => getDir(state));
	const detailView = useSelector(state => state.appState.persisted?.detailView);
	const eventLookups = useSelector(state => state.eventLookups);
	const alertFilterSettings = useSelector(state => state.appState.persisted?.alertFilterSettings);
	const disableAlertUnack = useSelector(state => getEcoSettingByKey("disableAlertUnack")(state) === true);
	const sortAsc = useSelector(state => getEcoSettingByKey("alarmListSortAscending")(state) === true);
	const showDeviceStateColumn = useSelector(state => getEcoSettingByKey("alarmListShowDeviceState")(state) === true);

	const isProfileExpanded = useSelector(state => state?.appState?.contextPanel?.contextPanelData?.secondaryOpen);
	const deviceStateLookup = useSelector(state => state.globalData?.deviceState || {});

	const filterGroupBy = alertFilterSettings?.filterGroupBy;
	const types = alertFilterSettings?.types ?? eventLookups?.types;
	const priorities = alertFilterSettings?.priorities ?? eventLookups?.priorities;
	const notificationsWithAggregates = processAggregates(notifications);
	const activeNotifications = notificationsWithAggregates.filter(item =>
		forReplay ? !item.transactionClosed : !item.closed
	);
	const notificationsGroupHeaderName = getTranslation("global.dock.notifications.main.notificationGroupHeader");

	const [selectedNotification, setSelectedNotification] = useState({});
	const [externalNotification, setExternalNotification] = useState({});
	const [showAlertTimerColumn, setShowAlertTimerColumn] = useState(false);

	const actionIconsPosition = detailView ? 670 : 0;

	const styles = {
		desc: {
			display: "flex",
			height: "100%",
			alignItems: "center"
		},
		spriteIcon: {
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			padding: 10,
			borderRadius: 5,
			height: 28,
			width: 28,
			...(dir === "rtl" ? { marginLeft: 30 } : { marginRight: 30 })
		},
		acknowledge: {
			height: 30,
			color: "#fff",
			minWidth: 0,
			maxWidth: "100%",
			padding: "0 8px",
			fontSize: 14,
			margin: "0px 5px",

			".MuiButton-label, & > span": {
				overflow: "hidden",
				textOverflow: "ellipsis",
				whiteSpace: "nowrap",
				width: 150
			},
			borderRadius: "5px",
			"&:hover": {
				backgroundColor: "#85C7EF !important"
			}
		},
		unAcknowledge: {
			height: 30,
			color: "#fff",
			minWidth: 0,
			maxWidth: "100%",
			padding: "0 8px",
			fontSize: 14,
			margin: "0px 5px",
			backgroundColor: "unset",
			border: "1px solid white",
			borderRadius: "5px",
			".MuiButton-label, & > span": {
				overflow: "hidden",
				textOverflow: "ellipsis",
				whiteSpace: "nowrap",
				width: 150
			},
			"&:hover": {
				backgroundColor: "#5E6265 !important"
			}
		},
		paperProps: {
			border: "unset",
			backgroundColor: "transparent",
			position: "fixed!important",
			height: "calc(100vh - 48px)",
			top: "48px",
			overflow: " visible !important",
			zIndex: 599,
			...(isBottomDock && { width: 670 }),
			...(dir === "rtl" && { boxShadow: "5px 0 6px #0000008b" }),
			...(dir === "ltr" && { boxShadow: "-5px 0 6px #0000008b" })
		},
		actionIcons: {
			position: "absolute",
			top: 60,
			...(dir === "rtl" ? { left: actionIconsPosition } : { right: actionIconsPosition })
		},

		pendingConcurrence: {
			height: 30,
			color: "#fff",
			minWidth: 0,
			maxWidth: "100%",
			padding: "0 8px",
			fontSize: 12,
			margin: "0px 5px",
			border: "1px solid #4B4B4C",
			borderRadius: "5px",

			".MuiButton-label, & > span": {
				overflow: "hidden",
				textOverflow: "ellipsis",
				whiteSpace: "nowrap",
				width: 150
			},
			"&.Mui-disabled": {
				color: "#939394"
			}
		},
		descriptionText: {
			overflow: "hidden",
			textOverflow: "ellipsis",
			fontSize: 14
		},
		targetingIconContainer: {
			padding: 0,
			marginTop: 2
		},
		alertActionButtonWrapper: {
			display: "flex",
			minWidth: 0,
			alignItems: "center",
			justifyContent: "flex-end",
			height: "100%",
			width: "100%"
		}
	};

	const customClasses = {
		"& .MuiDataGrid-columnHeaders": {
			boxShadow: "-5px 0px 6px #0000008B"
		},
		"& .MuiDataGrid-row--borderBottom": {
			background: "#2C2D2F !important"
		},
		"& .MuiDataGrid-cell": {
			color: "#FFFFFF",
			fontSize: "14px !important"
		},
		"& .MuiDataGrid-columnHeaderTitle": {
			color: "#FFFFFF",
			fontSize: 14
		}
	};

	useEffect(() => {
		const hasAlertWithTimer = notifications.some(notification =>
			notification.actions?.some(action => action.ackTimerEnabled)
		);
		setShowAlertTimerColumn(hasAlertWithTimer);
	}, [notifications]);

	const handleAcknowledge = notification => {
		const isAcked = notification?.occurrenceCount === notification?.ackedCount;
		const alertPayload = [{ alertId: notification?.id, occurrenceId: "*" }];
		if (isAcked) {
			if (notification?.eventPriorityId) {
				dispatch(unAcknowledgeAlerts(alertPayload));
			} else {
				dispatch(unAcknowledgeNotifications(alertPayload));
			}
		} else {
			if (notification?.eventPriorityId) {
				dispatch(acknowledgeAlerts(alertPayload));
				EventBus.publish("getAcknowledgedAlerts", {
					alertsData: notification,
					ackAll: false
				});
			} else {
				dispatch(acknowledgeNotifications(alertPayload));
			}
		}
	};

	const renderTargetingIcon = geometry => {
		return (
			<TargetingIcon
				geometry={geometry}
				defaultVisibility={true}
				iconContainerStyle={styles.targetingIconContainer}
			/>
		);
	};

	const renderDescWithTypeIcon = (desc, type) => {
		return (
			<div style={styles.desc}>
				<div
					style={{
						...styles.spriteIcon,
						backgroundColor: colors[type?.color]?.color ? colors[type?.color]?.color : "#979798"
					}}
				>
					<SpriteIcon
						iconName={type?.icon || "icon_information"}
						customHeight="17.5px"
						customWidth="17.5px"
					/>
				</div>
				<Typography style={styles.descriptionText}>{desc}</Typography>
			</div>
		);
	};

	const renderAcknowledgeButton = notification => {
		const isAcked = notification?.occurrenceCount === notification?.ackedCount;
		const isAggregate = (notification?.occurrenceCount || 1) > 1;
		// If concurrence requested, show "Pending Concurrence" text
		if (notification?.concurrence?.request) {
			return (
				<Box sx={styles.alertActionButtonWrapper}>
					<Button className="pendingBtn" sx={styles.pendingConcurrence} disabled variant="contained">
						<Translate value="global.sharedComponents.bottomAlarmList.pendingConcurrence" />
					</Button>
				</Box>
			);
		}
		// If aggregate, show acked/total count
		else if (isAggregate) {
			return `${notification.ackedCount || 0}/${notification.occurrenceCount} ${getTranslation("global.sharedComponents.bottomAlarmList.acknowledged")}`;
		}
		// If acknowledged && disableAlertUnack is false, show unacknowledge button
		else if (isAcked && !disableAlertUnack) {
			return (
				<Box sx={styles.alertActionButtonWrapper}>
					<Button
						className="ackBtn"
						sx={styles.unAcknowledge}
						onClick={e => {
							e.stopPropagation();
							handleAcknowledge(notification);
						}}
						variant="contained"
						color="primary"
					>
						<Translate value="global.sharedComponents.bottomAlarmList.unacknowledge" />
					</Button>
				</Box>
			);
		}
		// If acknowledged && disableAlertUnack is true, show "Acknowledged" text
		else if (isAcked && disableAlertUnack) {
			return <Translate value="global.sharedComponents.bottomAlarmList.acknowledged" />;
		}
		// If not acknowledged, show acknowledge button
		else if (!isAcked) {
			return (
				<Box sx={styles.alertActionButtonWrapper}>
					<Button
						className="ackBtn"
						sx={styles.acknowledge}
						onClick={e => {
							e.stopPropagation();
							handleAcknowledge(notification);
						}}
						variant="contained"
						color="primary"
					>
						<Translate value="global.sharedComponents.bottomAlarmList.acknowledge" />
					</Button>
				</Box>
			);
		} else {
			return <Fragment />;
		}
	};

	const renderAlertTimer = notification => {
		return notification ? (
			<AlertTimerCell
				alertActions={notification.actions}
				alertCreatedDate={notification.createdDate}
				acknowledged={notification.occurrenceCount === notification.ackedCount}
			/>
		) : (
			<Fragment />
		);
	};

	const handleRowClick = e => {
		const selectedNotification = e?.row?.acknowledge?.notification;
		if (selectedNotification) {
			dispatch(openAlertProfile(selectedNotification, forReplay, null, disableNotificationPanel));
			viewAlertPanelDetail(selectedNotification);
		}
	};

	const viewAlertPanelDetail = data => {
		if (selectedNotification?.id === data?.id) return; // validating if selected notification is being reselected, to avoid unnecessary rerenders in alert list.
		// setSelectedNotification({});
		setSelectedNotification(data);
		dispatch(setLocalAppState("detailView", true));
	};

	const closeDetailView = () => {
		dispatch(setLocalAppState("detailView", false));
		debounce(() => {
			setSelectedNotification({});
			setExternalNotification({});
		}, 200)();
	};

	useEffect(() => {
		EventBus.subscribe("openAlertProfile", setExternalNotification);
		return () => {
			EventBus.unsubscribe("openAlertProfile", setExternalNotification);
		};
	}, []);

	useEffect(() => {
		viewAlertPanelDetail(externalNotification);
	}, [externalNotification]);

	const columns = useMemo(() => [
		!disableTargetingIcons && {
			field: "targetingIcon",
			headerName: "",
			flex: 0.5,
			sortable: false,
			disableColumnMenu: true,
			align: "center",
			renderCell: params => params.value && renderTargetingIcon(params.value.geometry) // prop used to render jsx elements into mui data-grid.
		},
		{
			field: "description",
			headerName: getTranslation("global.sharedComponents.bottomAlarmList.columns.description"),
			flex: isProfileExpanded ? 3 : 5,
			disableColumnMenu: true,
			sortable: false,
			renderCell: params => params.value && renderDescWithTypeIcon(params.value.desc, params.value.type)
		},
		{
			field: "type",
			headerName: getTranslation("global.sharedComponents.bottomAlarmList.columns.type"),
			flex: isProfileExpanded ? 0.8 : 2.5,
			disableColumnMenu: true,
			sortable: false,
			groupingValueGetter: params => (!params ? notificationsGroupHeaderName : params) // grouping rows without a type property under 'Notifications'.
		},
		{
			field: "priority",
			headerName: getTranslation("global.sharedComponents.bottomAlarmList.columns.priority"),
			flex: isProfileExpanded ?  0.8 : 2,
			disableColumnMenu: true,
			sortable: false,
			groupingValueGetter: params => (!params ? notificationsGroupHeaderName : params) // grouping rows without a priority property under 'Notifications'.
		},
		showDeviceStateColumn && {
			field: "deviceState",
			headerName: getTranslation("global.sharedComponents.bottomAlarmList.columns.deviceState"),
			flex: isProfileExpanded ? 1 : 2,
			disableColumnMenu: true,
			sortable: false
		},
		{
			field: "count",
			headerName: getTranslation("global.sharedComponents.bottomAlarmList.columns.count"),
			flex: 1,
			sortable: false,
			disableColumnMenu: true
		},
		{
			field: "timeDate",
			headerName: getTranslation("global.sharedComponents.bottomAlarmList.columns.timeDate"),
			flex: isProfileExpanded ? 1 : 2,
			sortable: false,
			disableColumnMenu: true
		},
		showAlertTimerColumn && {
			field: "alertTimers",
			headerName: getTranslation("global.sharedComponents.bottomAlarmList.columns.alarmTimer"),
			flex: isProfileExpanded ? 1 : 3,
			disableColumnMenu: true,
			sortable: false,
			renderCell: params => params.value && renderAlertTimer(params.value.notification)
		},
		{
			field: "acknowledge",
			headerName: "",
			flex: 2.5,
			sortable: false,
			disableColumnMenu: true,
			align: "center",
			renderCell: params => params.value && renderAcknowledgeButton(params.value.notification)
		}
	].filter(Boolean), [
		disableTargetingIcons, 
		isProfileExpanded, 
		showAlertTimerColumn, 
		showDeviceStateColumn,
		notificationsGroupHeaderName
	]);

	const sortedNotifications = useMemo(() => {
		const isNoGrouping = !filterGroupBy || filterGroupBy === "none";

		// This line is used because, for new users, the filterEnabled property will not be present in the types unless the user makes changes to the filter settings (event types toggle on and off).
		const isFilterEnabled = item => item.filterEnabled ?? item.enabled;
		const byCreatedDate = sortAsc
			? (a, b) => new Date(a.createdDate) - new Date(b.createdDate)
			: (a, b) => new Date(b.createdDate) - new Date(a.createdDate);

		if (isNoGrouping) {
			// Still apply type filtering
			return activeNotifications
				.filter(notification =>
					types.some(type => type.id === notification.eventTypeId && isFilterEnabled(type))
				)
				.sort(byCreatedDate);
		}
		const lookupList = [
			...(filterGroupBy == "priority" ? priorities : types),
			{ name: notificationsGroupHeaderName, id: "default", filterEnabled: true, order: 9999 }
		];
		const lookupProp = filterGroupBy == "priority" ? "eventPriorityId" : "eventTypeId";

		// Group notifications
		const groups = activeNotifications.reduce((acc, notification) => {
			const groupName =
				lookupList.find(item => item.id === notification[lookupProp])?.name || notificationsGroupHeaderName;
			(acc[groupName] = acc[groupName] || []).push(notification);
			return acc;
		}, {});

		// Sort lookupList if grouping by priority
		if (filterGroupBy === "priority") {
			lookupList.sort((a, b) => a.order - b.order);
		}

		// Flatten sorted groups and sort notifications within enabled groups
		const final = lookupList
			.filter(group => group.filterEnabled && groups[group.name]) // Filter groups first
			.flatMap(group =>
				groups[group.name]
					.filter(notification =>
						types.some(type => type.id === notification.eventTypeId && type.filterEnabled)
					) // Filter notifications
					.sort((a, b) => {
						if (a.eventTypeId !== b.eventTypeId) {
							// Sort by eventTypeId order
							const orderA = types.findIndex(type => type.id === a.eventTypeId);
							const orderB = types.findIndex(type => type.id === b.eventTypeId);
							return orderA - orderB;
						}
						return byCreatedDate(a, b);
					})
			);

		return final;
	}, [
		activeNotifications,
		filterGroupBy,
		types,
		priorities,
		sortAsc,
		notificationsGroupHeaderName
	])

	const rows = useMemo(() => {
		return sortedNotifications
			?.map(notification => {
				if (!notification) return;

				const type = types?.find(item => item.id === notification.eventTypeId);
				const priority = priorities?.find(item => item.id === notification.eventPriorityId);
				const timeDate = timeConversion.convertToUserTime(
					notification.activity?.activityDate || notification.createdDate,
					`full_${timeFormatPreference}`,
					locale
				);

				const isDeviceTarget =
					showDeviceStateColumn && ["device", "camera"].includes(notification?.activity?.target?.entityType);

				const targetProps = isDeviceTarget ? notification?.activity?.target?.entityData?.properties : null;

				const deviceStateId = targetProps?.deviceState ?? "";
				const deviceState =
					isDeviceTarget && deviceStateId ? deviceStateLookup?.[deviceStateId]?.name || "" : "";
				return {
					id: notification.id,
					targetingIcon: { geometry: notification.geometry },
					description: { desc: notification.message, type: type },
					type: type?.name,
					deviceState,
					priority: priority?.name,
					count: notification.occurrenceCount || 1,
					timeDate: timeDate,
					alertTimers: { notification: notification },
					acknowledge: { notification: notification }
				};
			}
		).filter(Boolean)}, [
			sortedNotifications,
			timeFormatPreference,
			locale,
			showDeviceStateColumn,
			deviceStateLookup
		]);

	return (
		<>
			<DataGrid
				columns={columns}
				rows={rows}
				onRowClick={handleRowClick}
				rowGroupingModel={[filterGroupBy === "none" ? "" : filterGroupBy]}
				groupingColDef={{
					headerClassName: "group-column-header",
					cellClassName: "group-column-cell",
					width: 10
				}}
				getCellClassName={props =>
					props.rowNode.type == "group" &&
					`row-group ${props.field == "__row_group_by_columns_group__" ? "row-grouping-cell" : ""}`
				} // adding custom class to override styling in grouping header.
				customClasses={customClasses}
			/>
			<Drawer
				open={detailView}
				anchor={dir === "rtl" ? "left" : "right"}
				variant="persistent"
				PaperProps={{ style: styles.paperProps, className: "alert-panel-slideout" }}
			>
				{detailView && selectedNotification?.id && (
					<ErrorBoundary>
						<AlertPanelDetailWidget
							key={selectedNotification.id}
							readOnly={readOnly}
							notificationId={selectedNotification.id}
							closeDetailView={closeDetailView}
							selectFloorPlanOn={selectFloorPlanOn}
							floorPlansWithFacilityFeed={floorPlansWithFacilityFeed}
							notifications={activeNotifications}
							dir={dir}
							isExpanded={isBottomDock}
						/>
					</ErrorBoundary>
				)}
			</Drawer>
			{actionIcons && (
				<div style={styles.actionIcons}>
					<FilterSettings customStyles={{ margin: "4px 0 0 0" }} />
					<SettingsMenu
						notifications={notifications}
						bottomAlertListEnabled={true}
						customStyles={{ margin: "0 10px" }}
						disableDockOptions={true}
					/>
				</div>
			)}
		</>
	);
};

BottomAlertList.propTypes = propTypes;
BottomAlertList.defaultProps = defaultProps;

export default BottomAlertList;
